import { NmLoader } from '../NmLoader';
import { presentHealthMetricName } from '../../utils/healthMetricPresentation';
import { displayDigits } from '../../utils/displayDigits';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoChevronForwardOutline,
  IoChevronBackOutline,
  IoBarChartOutline,
  IoFlashOutline,
  IoHeartOutline,
  IoLinkOutline,
  IoPersonOutline,
  IoPulseOutline,
  IoSpeedometerOutline,
} from 'react-icons/io5';
import { useLocale } from '../../hooks/useLocale';
import { useAuth } from '../../hooks/useAuth';
import {
  athleteSettingsApi,
  type AthleteDetail,
  type MetricOption,
  type ZoneMethod,
} from '../../services/athleteSettingsApi';
import { NmButton } from '../NmButton';
import { NmMenuCard, NmMenuRow, NmSectionLabel } from '../NmMenuList';
import { NmMenuIcon } from '../NmMenuIcon';
import { useConfirm } from '../NmConfirmDialog';
import {
  buildSpeedZoneCalculatePayload,
  formatZonePaceRange,
  formatPaceSeconds,
  isDistanceTimeMethod,
  parseRaceTimeString,
} from '../../utils/paceZoneHelpers';
import { displayInputDigits, normalizeInputDigits } from '../../utils/localizedInput';
import { TrainingZoneEditor, TrainingZoneField, TrainingZoneRange } from './TrainingZoneEditor';
import { PowerZoneEditor } from './PowerZoneEditor';
import { PaceZoneCalculator } from './PaceZoneCalculator';
import { ConnectionsPanel } from './ConnectionsPanel';
import {
  CustomZoneBuilder,
  EditableZoneTable,
  type ZoneRow,
} from './ZoneCustomization';

type View =
  | 'menu'
  | 'account'
  | 'zones-menu'
  | 'zones-hr'
  | 'zones-power'
  | 'zones-speed'
  | 'metrics'
  | 'connections';

export type AthleteSettingsView = View;

interface AthleteSettingsModalProps {
  athleteId: number;
  onClose: () => void;
  onSaved?: () => void;
  presentation?: 'modal' | 'page';
  initialView?: AthleteSettingsView;
}

const HEART_RATE_TYPE = 1;
const POWER_TYPE = 2;
const SPEED_TYPE = 3;

// Each athlete owns a separate form instance. Late requests for the previous
// athlete cannot populate the newly selected athlete's fields or zone lists.
export function AthleteSettingsModal(props: AthleteSettingsModalProps) {
  return <AthleteSettingsContent key={props.athleteId} {...props} />;
}

function AthleteSettingsContent({
  athleteId,
  onClose,
  onSaved,
  presentation = 'modal',
  initialView = 'menu',
}: AthleteSettingsModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEditAccount = user?.id === athleteId;
  const { dir, isRtl } = useLocale();
  const [view, setView] = useState<View>(initialView);
  const [athlete, setAthlete] = useState<AthleteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const loadAthlete = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [athletesRes, detailsRes] = await Promise.all([
        athleteSettingsApi.getAthletes(),
        athleteSettingsApi.getAthleteDetails(athleteId).catch(() => null),
      ]);
      const list = athletesRes.data.data.athletes ?? [];
      const fromList = list.find((a) => a.id === athleteId) ?? null;
      const fromDetails = detailsRes?.data?.data?.details ?? null;
      setAthlete(fromDetails ?? fromList);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athleteSettings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [athleteId, t]);

  useEffect(() => {
    void loadAthlete();
    setView(initialView);
  }, [loadAthlete, athleteId, initialView]);

  const isPage = presentation === 'page';

  function handleBack() {
    if (view === initialView) {
      onClose();
      return;
    }
    if (view === 'zones-hr' || view === 'zones-power' || view === 'zones-speed') {
      setView('zones-menu');
      return;
    }
    if (view !== 'menu' && view !== initialView) {
      setView(initialView);
      return;
    }
    onClose();
  }

  function pushToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  }

  function handleSaved(msg = t('athleteSettings.saved')) {
    pushToast(msg);
    onSaved?.();
    void loadAthlete();
  }

  const title = athlete?.full_name ?? t('athleteSettings.title');
  const zonePage = isPage ? {
    'account': { title: t('athleteSettings.menu.account'), Icon: IoPersonOutline },
    'metrics': { title: t('athleteSettings.menu.metrics'), Icon: IoBarChartOutline },
    'connections': { title: t('athleteSettings.menu.connections'), Icon: IoLinkOutline },
    'zones-menu': { title: t('athleteSettings.menu.zones'), Icon: IoPulseOutline },
    'zones-hr': { title: t('athleteSettings.trainingEditor.heartTitle'), Icon: IoHeartOutline },
    'zones-power': { title: t('athleteSettings.powerEditor.title'), Icon: IoFlashOutline },
    'zones-speed': { title: t('athleteSettings.trainingEditor.paceTitle'), Icon: IoSpeedometerOutline },
  }[view as 'account' | 'metrics' | 'connections' | 'zones-menu' | 'zones-hr' | 'zones-power' | 'zones-speed'] : undefined;

  const panel = (
    <div
      dir={dir}
      data-settings-view={isPage ? view : undefined}
      className={`athlete-settings-modal title-bar-no-drag${isPage ? ' athlete-settings-modal--page' : ''}`}
      onClick={isPage ? undefined : (e) => e.stopPropagation()}
    >
      <header className="athlete-settings-modal__header">
        <div className="athlete-settings-modal__header-main">
          {(view !== 'menu' || isPage) && (
            <button
              type="button"
              className="athlete-settings-modal__back-btn"
              onClick={handleBack}
              aria-label={t('common.back', 'Back').replace(/[←→]/g, '').trim()}
            >
              {isRtl ? <IoChevronForwardOutline aria-hidden="true" /> : <IoChevronBackOutline aria-hidden="true" />}
            </button>
          )}
          {zonePage ? <div className={`athlete-settings-modal__zone-title${view === 'zones-menu' ? ' athlete-settings-modal__zone-title--menu' : ''}`}>
            <span className="power-zone-editor__icon"><zonePage.Icon aria-hidden="true" /></span>
            <h1>{zonePage.title}</h1>
          </div> : <div style={{ flex: 1, minWidth: 0 }}>
            <div className="athlete-settings-modal__title">{title}</div>
            <div className="athlete-settings-modal__subtitle">{t('athleteSettings.title')}</div>
          </div>}
        </div>
        {!isPage && (
          <button
            type="button"
            className="athlete-settings-modal__close-btn"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
          >
            ✕
          </button>
        )}
      </header>

      {toast && <div className="athlete-settings-modal__toast">{toast}</div>}

      <div className="athlete-settings-modal__body">
        {loading && !athlete ? (
          <div style={s.center}><NmLoader size="sm" inline label={t('common.loading')} /></div>
        ) : error && !athlete ? (
          <div style={s.center}>
            <p style={{ color: 'var(--fg-3)', marginBottom: 12 }}>{error}</p>
            <NmButton variant="primary" onClick={() => void loadAthlete()}>{t('athleteSettings.retry')}</NmButton>
          </div>
        ) : athlete ? (
          <>
            {view === 'menu' && (
              <SettingsMenu
                athlete={athlete}
                canEditAccount={canEditAccount && athlete.id === athleteId}
                onNavigate={setView}
              />
            )}
            {view === 'account' && canEditAccount && athlete.id === athleteId && (
              <AccountSection athlete={athlete} onSaved={handleSaved} />
            )}
            {view === 'zones-menu' && (
              <ZonesMenu onNavigate={setView} />
            )}
            {view === 'zones-hr' && (
              <HeartRateZonesSection athleteId={athlete.id} setting={athlete.setting} onSaved={handleSaved} />
            )}
            {view === 'zones-power' && (
              <PowerZonesSection athleteId={athlete.id} setting={athlete.setting} onSaved={handleSaved} />
            )}
            {view === 'zones-speed' && (
              <SpeedZonesSection athleteId={athlete.id} setting={athlete.setting} onSaved={handleSaved} />
            )}
            {view === 'metrics' && (
              <MetricsSection athlete={athlete} onSaved={handleSaved} />
            )}
            {view === 'connections' && (
              <ConnectionsPanel
                athleteId={athlete.id}
                connectedApps={athlete.connected_apps}
                variant="settings"
                onStatusChange={(apps) => {
                  setAthlete((prev) => (prev ? { ...prev, connected_apps: apps } : prev));
                }}
                onMessage={handleSaved}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );

  if (isPage) {
    return <div className="mobile-screen-scroll title-bar-no-drag">{panel}</div>;
  }

  return (
    <div className="athlete-settings-modal__backdrop title-bar-no-drag" onClick={onClose}>
      {panel}
    </div>
  );
}

// ─── Menu ─────────────────────────────────────────────────────────────────────

function SettingsMenu({
  athlete,
  canEditAccount,
  onNavigate,
}: {
  athlete: AthleteDetail;
  canEditAccount: boolean;
  onNavigate: (v: View) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="athlete-settings-modal__pill">
        {athlete.profile_image ? (
          <img src={athlete.profile_image} alt="" className="athlete-settings-modal__pill-avatar" />
        ) : (
          <span className="athlete-settings-modal__pill-initial">{athlete.full_name.charAt(0)}</span>
        )}
        <span className="athlete-settings-modal__pill-name">{athlete.full_name}</span>
      </div>

      <NmSectionLabel label={t('athleteSettings.title')} />
      <NmMenuCard>
        {canEditAccount && <NmMenuRow
          icon={<NmMenuIcon icon={IoPersonOutline} tone="pink" />}
          label={t('athleteSettings.menu.account')}
          description={t('athleteSettings.menu.accountDesc')}
          onClick={() => onNavigate('account')}
        />}
        <NmMenuRow
          icon={<NmMenuIcon icon={IoPulseOutline} tone="green" />}
          label={t('athleteSettings.menu.zones')}
          description={t('athleteSettings.menu.zonesDesc')}
          onClick={() => onNavigate('zones-menu')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoBarChartOutline} tone="cyan" />}
          label={t('athleteSettings.menu.metrics')}
          description={t('athleteSettings.menu.metricsDesc')}
          onClick={() => onNavigate('metrics')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoLinkOutline} tone="gold" />}
          label={t('athleteSettings.menu.connections')}
          description={t('athleteSettings.menu.connectionsDesc')}
          onClick={() => onNavigate('connections')}
          last
        />
      </NmMenuCard>
    </div>
  );
}

function ZonesMenu({ onNavigate }: { onNavigate: (v: View) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <NmSectionLabel label={t('athleteSettings.menu.zones')} />
      <NmMenuCard>
        <NmMenuRow
          icon={<NmMenuIcon icon={IoHeartOutline} tone="pink" />}
          label={t('athleteSettings.menu.heartZonesLabel')}
          onClick={() => onNavigate('zones-hr')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoFlashOutline} tone="gold" />}
          label={t('athleteSettings.menu.powerZonesLabel')}
          onClick={() => onNavigate('zones-power')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoSpeedometerOutline} tone="cyan" />}
          label={t('athleteSettings.menu.paceZonesLabel')}
          onClick={() => onNavigate('zones-speed')}
          last
        />
      </NmMenuCard>
    </div>
  );
}

// ─── Account ──────────────────────────────────────────────────────────────────

function AccountSection({
  athlete,
  onSaved,
}: {
  athlete: AthleteDetail;
  onSaved: (msg?: string) => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [firstName, setFirstName] = useState(athlete.first_name ?? '');
  const [lastName, setLastName] = useState(athlete.last_name ?? '');
  const [email, setEmail] = useState(athlete.email ?? '');
  const [weight, setWeight] = useState(String((athlete.setting as { weight?: number })?.weight ?? ''));
  const [birthday, setBirthday] = useState(athlete.birthday?.slice(0, 10) ?? '');
  const [gender, setGender] = useState<number | ''>(athlete.gender ?? '');
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  async function save() {
    setBusy(true);
    try {
      await athleteSettingsApi.updateProfile(athlete.id, {
        first_name: firstName,
        last_name: lastName,
        email: email || undefined,
        birthday: birthday ? `${birthday} 00:00:00` : undefined,
        gender: gender !== '' ? gender : undefined,
      });
      const w = parseFloat(weight);
      if (!Number.isNaN(w) && w > 0) {
        await athleteSettingsApi.updateSetting(athlete.id, { weight: w });
      }
      onSaved(t('athleteSettings.profileUpdated'));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('athleteSettings.error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function onImagePicked(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      await athleteSettingsApi.updateProfileImage(athlete.id, file);
      onSaved(t('athleteSettings.photoUpdated'));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('athleteSettings.error.uploadFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function deletePhoto() {
    const ok = await confirm({
      title: t('athleteSettings.deletePhoto.title'),
      message: t('athleteSettings.deletePhoto.message'),
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await athleteSettingsApi.deleteProfileImage(athlete.id);
      onSaved(t('athleteSettings.photoRemoved'));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('athleteSettings.error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-account-form">
      <div className="settings-account-avatar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button type="button" style={s.avatarBtn} aria-label={t('athleteSettings.accountForm.changePhoto')} onClick={() => fileRef.current?.click()} disabled={busy}>
          {athlete.profile_image ? (
            <img src={athlete.profile_image} alt="" style={s.bigAvatar} />
          ) : (
            <span style={s.bigInitial}>{athlete.full_name.charAt(0)}</span>
          )}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onImagePicked(e.target.files?.[0])} />
        <div style={{ display: 'flex', gap: 8 }}>
          <NmButton variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>{t('athleteSettings.accountForm.changePhoto')}</NmButton>
          {athlete.profile_image && (
            <NmButton variant="ghost" size="sm" onClick={() => void deletePhoto()} disabled={busy}>{t('athleteSettings.accountForm.removePhoto')}</NmButton>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>{displayDigits(athlete.mobile ?? '')}</span>
      </div>

      <Field label={t('athleteSettings.accountForm.firstName')} value={firstName} onChange={setFirstName} />
      <Field label={t('athleteSettings.accountForm.lastName')} value={lastName} onChange={setLastName} />
      <Field label={t('athleteSettings.accountForm.email')} value={email} onChange={setEmail} type="email" />
      <Field label={t('athleteSettings.accountForm.weight')} value={weight} onChange={setWeight} type="number" />
      <Field label={t('athleteSettings.accountForm.birthday')} value={birthday} onChange={setBirthday} type="date" />
      <div style={{ marginBottom: 12 }}>
        <label htmlFor="account-gender" style={s.fieldLabel}>{t('athleteSettings.accountForm.gender')}</label>
        <select id="account-gender"
          value={gender === '' ? '' : String(gender)}
          onChange={(e) => setGender(e.target.value === '' ? '' : Number(e.target.value))}
          style={s.select}
        >
          <option value="">—</option>
          <option value="1">{t('athleteSettings.accountForm.male')}</option>
          <option value="2">{t('athleteSettings.accountForm.female')}</option>
        </select>
      </div>

      <NmButton variant="primary" onClick={() => void save()} disabled={busy}>
        {t(busy ? 'athleteSettings.accountForm.saving' : 'athleteSettings.accountForm.save')}
      </NmButton>
    </div>
  );
}

// ─── Heart rate zones ─────────────────────────────────────────────────────────

function HeartRateZonesSection({
  athleteId,
  setting,
  onSaved,
}: {
  athleteId: number;
  setting?: Record<string, unknown> | null;
  onSaved: (msg?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [methods, setMethods] = useState<ZoneMethod[]>([]);
  const [methodId, setMethodId] = useState<number | ''>('');
  const [ltHr, setLtHr] = useState(String((setting as { lt_hr?: number })?.lt_hr ?? ''));
  const [maxHr, setMaxHr] = useState(String((setting as { max_hr?: number })?.max_hr ?? ''));
  const [restHr, setRestHr] = useState(String((setting as { rest_hr?: number })?.rest_hr ?? ''));
  const [customLtHr, setCustomLtHr] = useState('');
  const [customMaxHr, setCustomMaxHr] = useState('');
  const [customRestHr, setCustomRestHr] = useState('');
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);

  const loadZones = useCallback(async () => {
    const res = await athleteSettingsApi.getAthleteZones(athleteId);
    const types = (res.data.data.types ?? []) as Array<{ type: number; name: string; methods?: ZoneMethod[] }>;
    const hr: ZoneMethod[] = [];
    types.forEach((t) => {
      if (t.type === HEART_RATE_TYPE && t.methods?.length) {
        t.methods.forEach((m) => hr.push({ ...m, groupName: t.name }));
      }
    });
    setMethods(hr);
    const z = res.data.data.zones as Record<string, unknown> | undefined;
    if (z) {
      setLtHr(String(z.lt_hr ?? ''));
      setMaxHr(String(z.max_hr ?? ''));
      setRestHr(String(z.rest_hr ?? ''));
      const hrZones = (z.heart_rate_zones as { zones?: ZoneRow[]; range_percent?: ZoneRow[] }) ?? {};
      setZones(hrZones.zones ?? hrZones.range_percent ?? []);
    }
    return z;
  }, [athleteId]);

  useEffect(() => {
    void loadZones().catch(() => {});
  }, [loadZones]);

  async function calculate() {
    if (!methodId) { alert(t('athleteSettings.trainingEditor.methodRequired')); return; }
    if (!ltHr) { alert(t('athleteSettings.trainingEditor.lthrRequired')); return; }
    setBusy(true);
    try {
      await athleteSettingsApi.calculateUserZones(athleteId, {
        calculation_method: methodId,
        zone_type: 'heart_rate',
        lt_hr: Number(ltHr),
        max_hr: maxHr ? Number(maxHr) : undefined,
        rest_hr: restHr ? Number(restHr) : undefined,
      });
      await loadZones();
      onSaved(t('athleteSettings.trainingEditor.heartCalculated'));
    } catch (e: unknown) {
      alert(t('athleteSettings.trainingEditor.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function saveCustomZones(customZones: ZoneRow[]) {
    if (!customLtHr) { alert(t('athleteSettings.trainingEditor.lthrRequired')); throw new Error(t('athleteSettings.trainingEditor.lthrRequired')); }
    await athleteSettingsApi.createAthleteZones(athleteId, {
      zone_type: 'heart_rate',
      lt_hr: Number(customLtHr),
      max_hr: customMaxHr ? Number(customMaxHr) : undefined,
      rest_hr: customRestHr ? Number(customRestHr) : undefined,
      zones: customZones,
    });
    await loadZones();
    onSaved(t('athleteSettings.trainingEditor.customSaved'));
  }

  async function saveZoneLabels(updated: ZoneRow[]) {
    setSavingLabels(true);
    try {
      await athleteSettingsApi.updateZoneLabels(athleteId, 'heart_rate', updated);
      await loadZones();
      onSaved(t('athleteSettings.trainingEditor.namesSaved'));
    } finally {
      setSavingLabels(false);
    }
  }

  return <TrainingZoneEditor kind="heart" customThreshold={{ value: customLtHr, label: t('athleteSettings.trainingEditor.lthr') }}
    methods={methods} methodId={methodId} onMethodChange={setMethodId}
    zones={zones} unit={t('athleteSettings.trainingEditor.bpm')}
    formatRange={zone => <TrainingZoneRange value={`${zone.minimum} – ${zone.maximum}`} unit={t('athleteSettings.trainingEditor.bpm')} />}
    busy={busy} savingLabels={savingLabels} onCalculate={calculate} onSaveLabels={saveZoneLabels} onSaveCustom={saveCustomZones}
    settings={<>
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.lthr')} value={ltHr} onChange={setLtHr} />
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.maxHr')} value={maxHr} onChange={setMaxHr} />
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.restHr')} value={restHr} onChange={setRestHr} />
    </>}
    customFields={<>
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.lthr')} value={customLtHr} onChange={setCustomLtHr} />
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.maxHr')} value={customMaxHr} onChange={setCustomMaxHr} />
      <TrainingZoneField unit={t('athleteSettings.trainingEditor.bpm')} label={t('athleteSettings.trainingEditor.restHr')} value={customRestHr} onChange={setCustomRestHr} />
    </>} />;
}

// ─── Power zones ──────────────────────────────────────────────────────────────

function PowerZonesSection({
  athleteId,
  setting,
  onSaved,
}: {
  athleteId: number;
  setting?: Record<string, unknown> | null;
  onSaved: (msg?: string) => void;
}) {
  const [savedFtp, setSavedFtp] = useState(0);
  const [methods, setMethods] = useState<ZoneMethod[]>([]);
  const [methodId, setMethodId] = useState<number | ''>('');
  const [ftp, setFtp] = useState(String((setting as { ftp?: number })?.ftp ?? ''));
  const [customFtp, setCustomFtp] = useState('');
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);

  const loadZones = useCallback(async () => {
    const res = await athleteSettingsApi.getAthleteZones(athleteId);
    const types = (res.data.data.types ?? []) as Array<{ type: number; name: string; methods?: ZoneMethod[] }>;
    const pw: ZoneMethod[] = [];
    types.forEach((t) => {
      if (t.type === POWER_TYPE && t.methods?.length) {
        t.methods.forEach((m) => pw.push({ ...m, groupName: t.name }));
      }
    });
    setMethods(pw);
    const z = res.data.data.zones as Record<string, unknown> | undefined;
    if (z) {
      const pwMeta = z.power_zones as { threshold?: number } | undefined;
      setFtp(String(z.ftp ?? pwMeta?.threshold ?? ''));
      setSavedFtp(Number(z.ftp ?? pwMeta?.threshold ?? 0));
      const pwZones = (z.power_zones as { zones?: ZoneRow[]; range_percent?: ZoneRow[] }) ?? {};
      setZones(pwZones.zones ?? pwZones.range_percent ?? []);
    }
    return z;
  }, [athleteId]);

  useEffect(() => {
    void loadZones().catch(() => {});
  }, [loadZones]);

  async function calculate() {
    if (!methodId) { alert('Select a calculation method'); return; }
    if (!ftp) { alert('Enter FTP'); return; }
    setBusy(true);
    try {
      await athleteSettingsApi.calculateUserZones(athleteId, {
        calculation_method: methodId,
        zone_type: 'power',
        ftp: Number(ftp),
      });
      await loadZones();
      onSaved('Power zones calculated');
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Calculation failed');
    } finally {
      setBusy(false);
    }
  }

  async function saveCustomZones(customZones: ZoneRow[]) {
    if (!customFtp) { alert('Enter FTP'); throw new Error('FTP required'); }
    await athleteSettingsApi.createAthleteZones(athleteId, {
      zone_type: 'power',
      ftp: Number(customFtp),
      zones: customZones,
    });
    await loadZones();
    setCustomFtp('');
    onSaved('Custom power zones saved');
  }

  async function saveZoneLabels(updated: ZoneRow[]) {
    setSavingLabels(true);
    try {
      await athleteSettingsApi.updateZoneLabels(athleteId, 'power', updated);
      await loadZones();
      onSaved('Zone names updated');
    } finally {
      setSavingLabels(false);
    }
  }

  return (
    <PowerZoneEditor
      ftp={ftp} onFtpChange={setFtp} methodId={methodId} onMethodChange={setMethodId}
      methods={methods} zones={zones} savedFtp={savedFtp} busy={busy}
      savingLabels={savingLabels} onCalculate={calculate} onSaveLabels={saveZoneLabels}
      customFtp={customFtp} onCustomFtpChange={setCustomFtp} onSaveCustom={saveCustomZones}
    />
  );
}

// ─── Pace / speed zones ─────────────────────────────────────────────────────

interface ZoneMethodExt extends ZoneMethod {
  calculation_type_id?: number;
}

function buildSpeedZonePayload(
  method: ZoneMethodExt,
  race5k: string,
  race10k: string,
  thresholdPace: string,
): Record<string, unknown> {
  return buildSpeedZoneCalculatePayload(method, race5k, race10k, thresholdPace);
}

function SpeedZonesSection({
  athleteId,
  setting,
  onSaved,
}: {
  athleteId: number;
  setting?: Record<string, unknown> | null;
  onSaved: (msg?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [methods, setMethods] = useState<ZoneMethodExt[]>([]);
  const [methodId, setMethodId] = useState<number | ''>('');
  const [race5k, setRace5k] = useState('');
  const [race10k, setRace10k] = useState('');
  const [thresholdPace, setThresholdPace] = useState('');
  const [customPace, setCustomPace] = useState('');
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [savedThresholdPace, setSavedThresholdPace] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);

  const selectedMethod = methods.find((m) => m.id === methodId);

  const loadZones = useCallback(async () => {
    const res = await athleteSettingsApi.getAthleteZones(athleteId);
    const types = (res.data.data.types ?? []) as Array<{ type: number; name: string; methods?: ZoneMethodExt[] }>;
    const spd: ZoneMethodExt[] = [];
    types.forEach((t) => {
      if (t.type === SPEED_TYPE && t.methods?.length) {
        t.methods.forEach((m) => spd.push({ ...m, groupName: t.name }));
      }
    });
    setMethods(spd);
    const z = res.data.data.zones as Record<string, unknown> | undefined;
    if (z) {
      const sd = (z.speed_zones as {
        zones?: ZoneRow[];
        range_percent?: ZoneRow[];
        threshold?: number;
        pace_seconds_per_km?: number;
      }) ?? {};
      setZones(sd.zones ?? sd.range_percent ?? []);
      if (sd.pace_seconds_per_km) {
        const m = Math.floor(sd.pace_seconds_per_km / 60);
        const s = Math.round(sd.pace_seconds_per_km % 60);
        const formatted = `${m}:${s.toString().padStart(2, '0')}`;
        setSavedThresholdPace(formatted);
        setThresholdPace(formatted);
      } else if (sd.threshold) {
        const paceSec = 1000 / sd.threshold;
        const m = Math.floor(paceSec / 60);
        const sec = Math.round(paceSec % 60);
        const formatted = `${m}:${sec.toString().padStart(2, '0')}`;
        setSavedThresholdPace(formatted);
        setThresholdPace(formatted);
      }
    }
    return z;
  }, [athleteId]);

  useEffect(() => {
    void loadZones().catch(() => {});
  }, [loadZones]);

  async function calculate() {
    if (!methodId || !selectedMethod) { alert(t('athleteSettings.trainingEditor.methodRequired')); return; }
    const payload = buildSpeedZonePayload(selectedMethod, race5k, race10k, thresholdPace);
    if (isDistanceTimeMethod(selectedMethod.calculation_type_id)) {
      if (!payload.race_5k_time && !payload.race_10k_time) {
        alert(t('athleteSettings.trainingEditor.raceRequired'));
        return;
      }
    } else {
      if (!payload.pace_seconds_per_km && !payload.speed) {
        alert(t('athleteSettings.trainingEditor.paceRequired'));
        return;
      }
    }
    setBusy(true);
    try {
      await athleteSettingsApi.calculateUserZones(athleteId, payload);
      await loadZones();
      onSaved(t('athleteSettings.trainingEditor.paceCalculated'));
    } catch (e: unknown) {
      alert(t('athleteSettings.trainingEditor.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function saveCustomZones(customZones: ZoneRow[]) {
    const paceSec = parseRaceTimeString(customPace);
    if (!paceSec) { alert(t('athleteSettings.trainingEditor.paceRequired')); throw new Error(t('athleteSettings.trainingEditor.paceRequired')); }
    await athleteSettingsApi.createAthleteZones(athleteId, {
      zone_type: 'speed',
      pace_seconds_per_km: paceSec,
      zones: customZones,
    });
    await loadZones();
    onSaved(t('athleteSettings.trainingEditor.customSaved'));
  }

  async function saveZoneLabels(updated: ZoneRow[]) {
    setSavingLabels(true);
    try {
      await athleteSettingsApi.updateZoneLabels(athleteId, 'speed', updated);
      await loadZones();
      onSaved(t('athleteSettings.trainingEditor.namesSaved'));
    } finally {
      setSavingLabels(false);
    }
  }

  return <TrainingZoneEditor kind="pace"
    methods={methods} methodId={methodId} onMethodChange={setMethodId}
    zones={zones} unit={t('athleteSettings.trainingEditor.metersSecond')}
    formatRange={zone => zone.minimum === 0 && zone.maximum > 0
      ? <TrainingZoneRange value={formatPaceSeconds(1000 / zone.maximum)} unit={t('athleteSettings.trainingEditor.slowerPace', { pace: '' }).trim()} />
      : <TrainingZoneRange value={formatZonePaceRange(zone.minimum, zone.maximum).replace('/km', '').trim()} unit={t('athleteSettings.trainingEditor.perKm')} />}
    busy={busy} savingLabels={savingLabels} onCalculate={calculate} onSaveLabels={saveZoneLabels} onSaveCustom={saveCustomZones}
    savedSummary={savedThresholdPace ? <>{t('athleteSettings.trainingEditor.savedThreshold')} <strong dir="ltr">{displayInputDigits(savedThresholdPace, i18n.language)}</strong> {t('athleteSettings.trainingEditor.perKm')}</> : undefined}
    settings={methodId !== '' ? <PaceZoneCalculator
      calculationTypeId={selectedMethod?.calculation_type_id}
      race5kTime={race5k} race10kTime={race10k} thresholdPaceTime={thresholdPace} savedThresholdPace={savedThresholdPace}
      onRace5kChange={setRace5k} onRace10kChange={setRace10k} onThresholdPaceChange={setThresholdPace}
    /> : null}
    customFields={<>
      <TrainingZoneField label={t('athleteSettings.trainingEditor.threshold')} kind="pace" value={customPace} onChange={setCustomPace} placeholder="5:15" />
      <p className="power-zone-editor__hint">{t('athleteSettings.trainingEditor.speedCustomHint')}</p>
    </>} />;
}

// ─── Metrics ──────────────────────────────────────────────────────────────────

function MetricsSection({
  athlete,
  onSaved,
}: {
  athlete: AthleteDetail;
  onSaved: (msg?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState<MetricOption[]>([]);
  const [selected, setSelected] = useState<number[]>(athlete.metrics ?? []);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const language = i18n.resolvedLanguage ?? i18n.language;
  const metricName = (metric: MetricOption) => displayDigits(presentHealthMetricName(language.startsWith('fa') && metric.persian_name?.trim() ? metric.persian_name : metric.name, language));
  const visible = options.filter(metric => (!onlySelected || selected.includes(metric.id)) && `${metricName(metric)} ${metric.name}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  function loadOptions() {
    setLoading(true); setLoadFailed(false);
    void athleteSettingsApi.getMetricOptions().then(res => setOptions(res.data.data.metrics ?? []))
      .catch(() => setLoadFailed(true)).finally(() => setLoading(false));
  }

  useEffect(() => { loadOptions(); }, []);

  function toggle(id: number) {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function save() {
    setBusy(true);
    try {
      await athleteSettingsApi.updateMetrics(athlete.id, selected);
      onSaved(t('athleteSettings.metrics.updated'));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('athleteSettings.metrics.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-metrics" aria-label={t('athleteSettings.menu.metrics')}>
      <p className="settings-metrics__intro">{t('athleteSettings.metrics.intro')}</p>
      <div className="settings-metrics__toolbar">
        <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('athleteSettings.metrics.search')} aria-label={t('athleteSettings.metrics.search')} />
        <button type="button" aria-pressed={onlySelected} onClick={() => setOnlySelected(value => !value)}>{t('athleteSettings.metrics.selectedOnly')}</button>
      </div>
      <div className="settings-metrics__summary" role="status">{displayDigits(t('athleteSettings.metrics.selectionCount', { count: selected.length }))}</div>
      <div className="settings-metrics__list" aria-busy={loading}>
        {loading ? <p role="status"><NmLoader size="sm" inline label={t('common.loading')} /></p> : loadFailed ? <div className="settings-metrics__empty"><p role="alert">{t('athleteSettings.loadFailed')}</p><NmButton onClick={loadOptions}>{t('athleteSettings.retry')}</NmButton></div> : visible.map(metric => (
          <label key={metric.id} className="settings-metrics__option" data-selected={selected.includes(metric.id)}>
            <input type="checkbox" checked={selected.includes(metric.id)} onChange={() => toggle(metric.id)} disabled={busy} />
            <span>{metricName(metric)}</span>
          </label>
        ))}
        {!loading && !loadFailed && !visible.length && <p className="settings-metrics__empty">{t(options.length ? 'athleteSettings.metrics.noResults' : 'athleteSettings.metrics.empty')}</p>}
      </div>
      <footer className="settings-metrics__footer"><NmButton variant="primary" onClick={() => void save()} disabled={busy || loading || loadFailed}>
        {busy ? t('athleteSettings.metrics.saving') : t('athleteSettings.metrics.save')}
      </NmButton></footer>
    </section>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function Field({
  label, value, onChange, type = 'text', placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  const { i18n } = useTranslation();
  const persianNumber = type === 'number' && i18n.language.split('-')[0] === 'fa';
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={s.fieldLabel}>{label}</label>
      <input
        aria-label={label}
          type={persianNumber ? 'text' : type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        value={persianNumber ? displayInputDigits(value, i18n.language) : value}
        placeholder={placeholder}
        onChange={(e) => onChange(type === 'number' ? normalizeInputDigits(e.target.value) : e.target.value)}
        style={s.input}
      />
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 },
  fieldLabel: { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', marginBottom: 4, textAlign: 'start' },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 'var(--r-2)',
    border: '1px solid var(--surface-sunken)', background: 'var(--surface-0)',
    fontSize: 13, color: 'var(--fg-1)', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '8px 10px', borderRadius: 'var(--r-2)',
    border: '1px solid var(--surface-sunken)', background: 'var(--surface-0)', fontSize: 13,
  },
  avatarBtn: { border: 'none', background: 'none', cursor: 'pointer', padding: 0 },
  bigAvatar: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' },
  bigInitial: {
    width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, var(--vo2-pink-400), var(--vo2-pink-600))',
    color: '#fff', fontSize: 28, fontWeight: 700,
  },
  metricRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
    borderRadius: 'var(--r-2)', background: 'var(--surface-sunken)', cursor: 'pointer', fontSize: 13,
  },
};
